import EventEmitter from 'eventemitter2';
import Crossroads from 'crossroads';
import { URI } from './url/URI.js';
import { baseurl } from './url/baseurl.js';

/**
 * Allow easily managing routing
 * @author Geoffrey Mayeux
 */
class Router extends EventEmitter{
	constructor(){
		super();

		this._crossroads  				= Crossroads.create();
        // this._crossroads.normalizeFn 	= Crossroads.NORM_AS_OBJECT;
        this._crossroads.ignoreState 	= true;
        this._crossroads.shouldTypecast = true;

        this._previous 	= undefined;
		this._current 	= new URI(window.location.href);

		this.routedHandler = null;

		this.exitHandlers = new WeakMap();

		if(!document.querySelector('base'))
			console.warn(`No <base> tag defined, Router may not work properly [computed base: ${baseurl()}].`);
	}

	/**
	 * Get the previous route
	 * @returns {Object} - The previous route
	 */
	get previous(){
		return this._previous;
	}

	/**
	 * Get the current route
	 * @returns {Object} - The current route
	 */
	get current(){
		return this._current;
	}

	/**
	 * Add a new route
	 * @param {string|RegExp} pattern - The route to add, refer to {@link https://millermedeiros.github.io/crossroads.js/#crossroads-add_route|crossroads.addRoute} method for pattern
	 * @param {function} handler - The handler that will be called when the pattern is matched. Will receive pattern matches as arguments.
	 * @returns {Object} - The created route
	 */
	add(pattern, enterHandler){
        const   route = this._crossroads.addRoute(pattern);

        if(typeof enterHandler == "function"){
	        if(this.routedHandler) route.matched.add(this.routedHandler, null, 10);
        	route.matched.add(enterHandler, null, 0);
        }


        return route;
	}

	/**
	 * Removes a route
	 * @param {Route} route - The route object given by the {@link Router#add} method
	 */
	remove(route){
        this._crossroads.removeRoute(route);
        route.dispose();
	}

	/**
	 * Adds a handler triggered when no registered route is matched
	 * @param {function} handler - The handler function
	 */
    catchAll(handler){
        this._crossroads.bypassed.add(handler);
    }

    removeCatchAll(handler){
    	this._crossroads.bypassed.remove(handler)
    }

    routed(handler, priority=10){
    	this.routedHandler = handler;
    	this._crossroads._routes.forEach(route => route.matched.add(this.routedHandler, null, 10))
    }

    removeRouted(handler){
    	this._crossroads._routes.forEach(route => route.matched.remove(this.routedHandler, null, 10));
    	this.routedHandler = null;
    }

	/**
	 * Set the current route
	 * @param {string} url - The url to change the route to
	 */
	set(url){
		url = new URI(typeof url == "string" ? url : "");

		if(this.current.equals(url))
			return;

		this._previous 	= this._current;
		this._current 	= url;

        this._crossroads.parse(this._current.relative);
		this.emit('change', this._current.toString());
	}
}

export default Router;
export { Router };